"use client";

import React, { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { ModoHistoriaService } from "../../../services/admin/modo-historia/modo-historia.service";

interface ImportarJSONModalModoHistoriaProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ImportarJSONModalModoHistoria: React.FC<
  ImportarJSONModalModoHistoriaProps
> = ({ isOpen, onClose, onSuccess }) => {
  const [jsonData, setJsonData] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleImport = async () => {
    if (!jsonData.trim()) {
      setError("Por favor, pega el JSON de las preguntas");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Validar que sea JSON válido
      const parsedData = JSON.parse(jsonData);

      // Validar estructura básica
      if (!parsedData.preguntas || !Array.isArray(parsedData.preguntas)) {
        throw new Error("El JSON debe contener un array 'preguntas'");
      }

      // Validar que no contenga mundoHistoriaId (que no es necesario)
      const preguntasConMundo = parsedData.preguntas.filter(
        (p: any) => p.mundoHistoriaId !== undefined
      );
      if (preguntasConMundo.length > 0) {
        console.warn(
          "Algunas preguntas contienen 'mundoHistoriaId' que no es necesario. Se ignorará este campo."
        );
      }

      // Validar campos requeridos en cada pregunta
      for (let i = 0; i < parsedData.preguntas.length; i++) {
        const pregunta = parsedData.preguntas[i];

        // Validar campos básicos requeridos
        if (!pregunta.contenido || !pregunta.respuesta) {
          throw new Error(
            `Pregunta ${i + 1}: Faltan campos requeridos (contenido, respuesta)`
          );
        }

        // Validar que tenga método de identificación de etapa
        const tieneEtapaId = pregunta.etapaHistoriaId !== undefined;
        const tieneMundoYEtapa =
          pregunta.mundoOrden !== undefined &&
          pregunta.etapaOrden !== undefined;

        if (!tieneEtapaId && !tieneMundoYEtapa) {
          throw new Error(
            `Pregunta ${
              i + 1
            }: Debe especificar etapaHistoriaId O mundoOrden + etapaOrden`
          );
        }
      }

      // Importar las preguntas usando el servicio
      await ModoHistoriaService.importarPreguntasDesdeJSON(parsedData);

      onSuccess();
      onClose();
      setJsonData("");
    } catch (err: any) {
      setError(err.message || "Error al procesar el JSON");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setJsonData("");
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Importar Preguntas desde JSON
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <Label
                htmlFor="jsonData"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                JSON de Preguntas
              </Label>
              <Textarea
                id="jsonData"
                value={jsonData}
                onChange={(e) => setJsonData(e.target.value)}
                placeholder="Pega aquí el JSON con las preguntas..."
                className="min-h-[300px] font-mono text-sm"
                rows={12}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">{error}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-blue-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Formato esperado
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      El JSON debe tener la siguiente estructura. Usa{" "}
                      <code>mundoOrden</code> y <code>etapaOrden</code> para
                      especificar la etapa:
                    </p>
                    <p className="mt-2">
                      El <code>preguntaOrden</code> determina la secuencia de
                      las preguntas dentro de la etapa.
                    </p>
                    <pre className="mt-2 text-xs bg-blue-100 p-2 rounded">
                      {`{
  "preguntas": [
    {
      "contenido": "Pregunta...",
      "alternativas": {
        "A": "Alternativa A",
        "B": "Alternativa B", 
        "C": "Alternativa C",
        "D": "Alternativa D"
      },
      "respuesta": "A",
      "explicacion": "Explicación...",
      "mundoOrden": 3,
      "etapaOrden": 6,
      "preguntaOrden": 1,
      "dificultad": 3,
      "fuente": "Importación JSON",
      "vigente": true
    }
  ]
}`}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <Button onClick={handleClose} variant="outline" disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleImport}
            disabled={isLoading || !jsonData.trim()}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? "Importando..." : "Importar"}
          </Button>
        </div>
      </div>
    </div>
  );
};
